/**
 * RolodexEffect creates a vertical flip-clock rotation transition between slides.
 *
 * Unlike announcement-bar (which uses opposite edges for in/out creating a cube),
 * rolodex pivots BOTH incoming and outgoing slides from the SAME edge,
 * creating an authentic flip-clock / card-holder feel.
 *
 * The effect uses two transition ranges (90° each):
 * - CL1 → L1: Leaving slide rotates from flat (0°) to edge-on (−90°)
 * - CR1 → R1: Entering slide rotates from edge-on (90°) down to flat (0°)
 *
 * Both slides pivot from the same edge (top or bottom), controlled by the
 * `rolodex.origin` option ('bottom' by default).
 */

// Factory function - class is created when TarotEffect is available
function createRolodexEffect(TarotEffect) {
	return class RolodexEffect extends TarotEffect {
		static effectName = 'rolodex';

		static defaultOptions = { rolodex: { origin: 'bottom' } };

		/**
		 * Effect configuration rules that define carousel behavior constraints
		 */
		static rules = {
			minSlidesPerView: 1,
			maxSlidesPerView: 1,
			loopBuffer: { left: 1, right: 1 },
		};

		/** @type {'bottom'|'top'} Pivot edge for all rotations (default: 'bottom') */
		#origin;
		/** @type {1|-1} Precomputed sign: +1 for top origin, -1 for bottom */
		#rotationSign;

		/**
		 * Creates a new rolodex effect instance
		 * @param {Object} ctx - The shared context object
		 */
		constructor(ctx) {
			super(ctx);
			this.init();
		}

		init() {
			super.init();
			this.#readOriginOption();
		}

		reInit() {
			super.reInit();
			this.#readOriginOption();
		}

		/**
		 * Read `rolodex.origin` option and precompute the rotation sign
		 */
		#readOriginOption() {
			const opts = this.ctx.store.getOptions().rolodex || {};
			this.#origin = opts.origin === 'top' ? 'top' : 'bottom';
			this.#rotationSign = this.#origin === 'top' ? 1 : -1;
		}

		/**
		 * Main render function called every animation frame to create flip-clock rotations
		 *
		 * @param {Object} frame - Complete frame object with all carousel data
		 * @param {Object} utils - Frame utilities for position calculations
		 */
		render(frame, utils) {
			const { slides, widths, animation } = frame;

			this.renderSlideWidth(widths.slide);

			for (let slideIndex = 0, totalSlides = slides.length; slideIndex < totalSlides; slideIndex++) {
				const slide = slides[slideIndex];

				// Check if slide is in the center visible position (CL1 → CR1)
				const centerRange = utils.isSlideInRange(slide, 'CR1', 'CL1');
				if (centerRange.isInRange) {
					this.#applyCenterFace(slide, animation.isAnimating, widths);
					continue;
				}

				// Check if slide is in the leaving transition range (CL1 → L1)
				// percent: 0 = at L1 (edge-on), 1 = at CL1 (flat)
				const leavingRange = utils.isSlideInRange(slide, 'CL1', 'L1');
				if (leavingRange.isInRange) {
					this.#applyLeavingFace(slide, leavingRange.percent, animation.isAnimating, widths);
					continue;
				}

				// Check if slide is in the entering transition range (CR1 → R1)
				// percent: 0 = at R1 (edge-on), 1 = at CR1 (flat)
				const enteringRange = utils.isSlideInRange(slide, 'R1', 'CR1');
				if (enteringRange.isInRange) {
					this.#applyEnteringFace(slide, enteringRange.percent, animation.isAnimating, widths);
					continue;
				}

				// Outside visible ranges - hide the slide
				this.#applyHiddenState(slide);
			}
		}

		/**
		 * Apply styling for center face (CL1 → CR1)
		 * The fully visible slide with no rotation
		 *
		 * @param {HTMLElement} slide - The slide element to transform
		 * @param {boolean} isAnimating - Whether carousel is currently animating
		 * @param {Object} widths - Width measurements including paddingLeft
		 */
		#applyCenterFace(slide, isAnimating, widths) {
			const translateX = widths.paddingLeft;

			const transformValue = isAnimating
				? `translate3d(${translateX}px, 0, 0)`
				: `translateX(${translateX}px)`;

			slide.style.transform = transformValue;
			slide.style.transformOrigin = `center ${this.#origin}`;
			slide.style.transformStyle = 'preserve-3d';
			slide.style.backfaceVisibility = 'hidden';
			slide.style.zIndex = '3';
			slide.style.opacity = '1';
			slide.show();
		}

		/**
		 * Apply flip-clock rotation for leaving slides (CL1 → L1)
		 * Slides rotate from flat (0°) to edge-on (90°) and fold away
		 *
		 * @param {HTMLElement} slide - The slide element to transform
		 * @param {number} transitionPercent - Progress (0 = at L1 edge-on, 1 = at CL1 flat — slide folding away)
		 * @param {boolean} isAnimating - Whether carousel is currently animating
		 * @param {Object} widths - Width measurements including paddingLeft
		 */
		#applyLeavingFace(slide, transitionPercent, isAnimating, widths) {
			// top origin (+1): rotates to +90° (top folds away from viewer)
			// bottom origin (-1): rotates to -90° (bottom folds away from viewer)
			const rotateAmount = this.#rotationSign * 90 * (1 - transitionPercent);

			const translateX = widths.paddingLeft;

			const transformValue = isAnimating
				? `translate3d(${translateX}px, 0, 0) rotateX(${rotateAmount}deg)`
				: `translateX(${translateX}px) rotateX(${rotateAmount}deg)`;

			slide.style.transform = transformValue;
			slide.style.transformOrigin = `center ${this.#origin}`;
			slide.style.transformStyle = 'preserve-3d';
			slide.style.backfaceVisibility = 'hidden';
			slide.style.zIndex = '2';
			slide.style.opacity = '1';
			slide.show();
		}

		/**
		 * Apply flip-clock rotation for entering slides (CR1 → R1)
		 * Slides rotate from edge-on (90°) down into view (0°)
		 *
		 * @param {HTMLElement} slide - The slide element to transform
		 * @param {number} transitionPercent - Progress (0 = at R1 edge-on, 1 = at CR1 flat — slide swinging into view)
		 * @param {boolean} isAnimating - Whether carousel is currently animating
		 * @param {Object} widths - Width measurements including paddingLeft
		 */
		#applyEnteringFace(slide, transitionPercent, isAnimating, widths) {
			// Hide the entering card for the first 45% of its journey — it's nearly edge-on
			// at that point, preventing peek-through before the flip becomes visible
			if (transitionPercent > 0.55) {
				this.#applyHiddenState(slide);
				return;
			}

			// top origin (+1): rotates to -90° (bottom folds away from viewer)
			// bottom origin (-1): rotates to +90° (top folds away from viewer)
			const rotateAmount = this.#rotationSign * -90 * transitionPercent;

			const translateX = widths.paddingLeft;

			const transformValue = isAnimating
				? `translate3d(${translateX}px, 0, 0) rotateX(${rotateAmount}deg)`
				: `translateX(${translateX}px) rotateX(${rotateAmount}deg)`;

			slide.style.transform = transformValue;
			slide.style.transformOrigin = `center ${this.#origin}`;
			slide.style.transformStyle = 'preserve-3d';
			slide.style.backfaceVisibility = 'hidden';
			slide.style.zIndex = '1';
			slide.style.opacity = '1';
			slide.show();
		}

		/**
		 * Hide slides that are outside the visible rotation ranges
		 *
		 * @param {HTMLElement} slide - The slide element to hide
		 */
		#applyHiddenState(slide) {
			slide.style.opacity = '0';
			slide.hide();
			slide.style.zIndex = '0';
			slide.style.transform = 'translate3d(0, 0, 0)';
			slide.style.transformOrigin = `center ${this.#origin}`;
			slide.style.transformStyle = '';
			slide.style.backfaceVisibility = '';
		}

		/**
		 * Clean up the effect when it's being destroyed or replaced
		 */
		destroy() {
			const slides = this.ctx.store.getSlides();
			for (let i = 0; i < slides.length; i++) {
				const slide = slides[i];
				slide.style.transform = '';
				slide.style.transformOrigin = '';
				slide.style.transformStyle = '';
				slide.style.backfaceVisibility = '';
				slide.style.opacity = '';
				slide.style.zIndex = '';
				slide.show?.();
			}

			super.destroy();
		}
	};
}

// Queue the factory (works regardless of whether Tarot is loaded)
(window.TarotEffectQueue = window.TarotEffectQueue || []).push({
	effectName: 'rolodex',
	factory: createRolodexEffect,
});

export default createRolodexEffect;
